import Badge from "common/Badge";
import DataTable_ from "common/DataTable";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getAll, SearchOptions } from "utils/queries";
import { FiEye, FiMoreVertical } from "react-icons/fi";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import SelectSearch from "common/SelectSearch";
import styles from "./installmentOrders.module.css";

const STATUS_STYLES = {
  "On Track": { background: "#E6F8F6", color: "#0A8F84" },
  Completed: { background: "#E8F5E9", color: "#2E7D32" },
  Overdue: { background: "#FDECEC", color: "#C62828" },
  // Cancelled: { background: "#EFEFF1", color: "#555" },
};

const STATUS_OPTIONS = [
  { value: "on_track", label: "On Track" },
  { value: "completed", label: "Completed" },
  { value: "overdue", label: "Overdue" },
  // { value: "cancelled", label: "Cancelled" },
];

const deriveInstallmentStatus = (row) => {
  const orderStatus = `${row?.order_status || ""}`.toLowerCase();
  // if (orderStatus === "cancelled" || orderStatus === "canceled") {
  //   return "Cancelled";
  // }
  if (orderStatus === "completed" || orderStatus === "complete") {
    return "Completed";
  }
  if (orderStatus === "overdue") {
    return "Overdue";
  }

  const paid = Number(row?.installment_paid) || 0;
  const total = Number(row?.installment_count) || 0;

  if (total > 0 && paid >= total) {
    return "Completed";
  }

  const nextDate = row?.next_installment_date
    ? new Date(row.next_installment_date)
    : null;

  if (nextDate && !Number.isNaN(nextDate.getTime()) && nextDate < new Date()) {
    return "Overdue";
  }

  return "On Track";
};

const formatDate = (value) => {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const InstallmentOrders = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialFromUrl = useMemo(() => {
    const statusParam = searchParams.get("istatus");
    return {
      search: searchParams.get("iq") || "",
      event: searchParams.get("ieventId")
        ? {
            value: searchParams.get("ieventId"),
            label: searchParams.get("ieventLabel") || "",
          }
        : null,
      status: statusParam
        ? STATUS_OPTIONS.find((o) => o.value === statusParam) || null
        : null,
      page: Math.max(1, Number(searchParams.get("ipage")) || 1),
      perPage: Math.max(1, Number(searchParams.get("iperPage")) || 10),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [displayCount, setDisplayCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState(initialFromUrl.search);
  const [selectedEvent, setSelectedEvent] = useState(initialFromUrl.event);
  const [selectedStatus, setSelectedStatus] = useState(initialFromUrl.status);
  const [currentPage, setCurrentPage] = useState(initialFromUrl.page);
  const [perPage, setPerPage] = useState(initialFromUrl.perPage);
  const [allOrders, setAllOrders] = useState([]);
  const [isFetchingAll, setIsFetchingAll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const childRef = useRef();
  const exportMenuRef = useRef(null);
  const didMountRef = useRef(false);
  const didFilterResetRef = useRef(false);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (searchTerm) next.set("iq", searchTerm);
        else next.delete("iq");
        if (selectedEvent?.value) {
          next.set("ieventId", `${selectedEvent.value}`);
          if (selectedEvent.label) {
            next.set("ieventLabel", selectedEvent.label);
          } else {
            next.delete("ieventLabel");
          }
        } else {
          next.delete("ieventId");
          next.delete("ieventLabel");
        }
        if (selectedStatus?.value) next.set("istatus", selectedStatus.value);
        else next.delete("istatus");
        if (currentPage > 1) next.set("ipage", `${currentPage}`);
        else next.delete("ipage");
        if (perPage !== 10) next.set("iperPage", `${perPage}`);
        else next.delete("iperPage");
        return next;
      },
      { replace: true }
    );
  }, [
    searchTerm,
    selectedEvent,
    selectedStatus,
    currentPage,
    perPage,
    setSearchParams,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        exportMenuRef.current &&
        !exportMenuRef.current.contains(event.target)
      ) {
        setIsExportMenuOpen(false);
      }
    };

    if (isExportMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isExportMenuOpen]);

  const fetchInstallments = useCallback(
    async ({ limit = 10, offset = 0 }) => {
      try {
        setIsLoading(true);
        setCurrentPage(Math.floor(offset / Math.max(1, limit)) + 1);
        setPerPage(limit);
        let query = `orders/admin/installments?offset=${offset}&limit=${limit}`;
        if (selectedEvent?.value) {
          query += `&event_id=${encodeURIComponent(selectedEvent.value)}`;
        }
        const result = await getAll(query);
        const orders = result?.data?.orders || [];
        const total = Number(orders?.[0]?.total_count) || 0;
        setData(orders);
        setTotalCount(total);
        if (offset === 0) {
          setAllOrders((prev) => {
            if (limit >= total && total) {
              return orders;
            }
            if (!prev.length) {
              return orders;
            }
            return prev;
          });
        }
      } catch (error) {
        console.error("Error fetching installment orders:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [selectedEvent?.value]
  );

  const fetchAllInstallments = useCallback(async () => {
    if (isFetchingAll) {
      return allOrders;
    }

    const totalRecords = totalCount || data.length;
    if (!totalRecords) {
      return allOrders;
    }

    try {
      setIsFetchingAll(true);
      let query = `orders/admin/installments?offset=0&limit=${totalRecords}`;
      if (selectedEvent?.value) {
        query += `&event_id=${encodeURIComponent(selectedEvent.value)}`;
      }
      const result = await getAll(query);
      const orders = result?.data?.orders || [];
      setAllOrders(orders);
      return orders;
    } catch (error) {
      console.error("Error fetching all installment orders:", error);
      return allOrders;
    } finally {
      setIsFetchingAll(false);
    }
  }, [allOrders, data.length, isFetchingAll, totalCount, selectedEvent?.value]);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      fetchInstallments({
        limit: initialFromUrl.perPage,
        offset: (initialFromUrl.page - 1) * initialFromUrl.perPage,
      });
    } else {
      fetchInstallments({ limit: 10, offset: 0 });
      childRef.current?.resetFields?.();
    }
    setAllOrders([]);
  }, [fetchInstallments, selectedEvent?.value, initialFromUrl.page, initialFromUrl.perPage]);

  useEffect(() => {
    if (!didFilterResetRef.current) {
      didFilterResetRef.current = true;
      return;
    }
    childRef.current?.resetFields?.();
    setCurrentPage(1);
    setPerPage(10);
  }, [searchTerm, selectedStatus?.value]);

  useEffect(() => {
    const trimmedSearch = searchTerm.trim();
    if (!trimmedSearch && !selectedStatus?.value) {
      setFilteredData(data);
      setDisplayCount(totalCount);
    }
  }, [data, totalCount, searchTerm, selectedStatus?.value]);

  useEffect(() => {
    let isMounted = true;

    const applyFilters = async () => {
      const trimmedSearch = searchTerm.trim();
      if (!trimmedSearch && !selectedStatus?.value) {
        return;
      }

      const sourceData = await (async () => {
        if (allOrders.length === totalCount && totalCount) {
          return allOrders;
        }

        const fullData = await fetchAllInstallments();
        if (fullData.length) {
          return fullData;
        }

        return allOrders.length ? allOrders : data;
      })();

      const term = trimmedSearch.toLowerCase();
      const filtered = sourceData.filter((order) => {
        const orderId = order?.order_id != null ? `${order.order_id}` : "";
        const name = order?.customer_name
          ? `${order.customer_name}`.toLowerCase()
          : "";

        const matchesSearch = !trimmedSearch
          ? true
          : orderId.includes(trimmedSearch) || name.includes(term);

        const matchesStatus = !selectedStatus?.value
          ? true
          : deriveInstallmentStatus(order)
              .toLowerCase()
              .replace(/\s+/g, "_") === selectedStatus.value;

        return matchesSearch && matchesStatus;
      });

      if (isMounted) {
        setFilteredData(filtered);
        setDisplayCount(filtered.length);
      }
    };

    applyFilters();

    return () => {
      isMounted = false;
    };
  }, [
    searchTerm,
    selectedStatus?.value,
    allOrders,
    data,
    totalCount,
    fetchAllInstallments,
  ]);

  const fetchEventOptions = async (search = "") => {
    try {
      if (!search || search.trim() === "") {
        const optionsResult = await SearchOptions(
          `event/events?eventType=upcoming&limit=100&offset=0&currentDate=${new Date().toISOString()}`
        );
        return (
          optionsResult?.data?.data?.rows?.map((e) => ({
            value: e?.id,
            label: e?.event_name,
          })) || []
        );
      }
      const optionsResult = await SearchOptions(
        `ticket/search?key=${encodeURIComponent(search)}`
      );
      const term = search.toLowerCase();
      return (
        optionsResult?.data?.data
          ?.filter((e) =>
            `${e?.event_name || ""}`.toLowerCase().includes(term)
          )
          ?.map((e) => ({
            value: e?.id,
            label: e?.event_name,
          })) || []
      );
    } catch (error) {
      console.error("Error fetching event options:", error);
      return [];
    }
  };

  const getExportSourceData = useCallback(async () => {
    const trimmedSearch = searchTerm.trim();
    if (trimmedSearch || selectedStatus?.value) {
      return filteredData;
    }

    const totalRecords = totalCount || data.length;
    if (!totalRecords) {
      return [];
    }

    if (allOrders.length === totalRecords && totalRecords) {
      return allOrders;
    }

    const fullData = await fetchAllInstallments();
    if (fullData?.length) {
      return fullData;
    }

    if (allOrders.length) {
      return allOrders;
    }
    return data;
  }, [
    allOrders,
    data,
    fetchAllInstallments,
    filteredData,
    searchTerm,
    selectedStatus?.value,
    totalCount,
  ]);

  const handleExportToPDF = async () => {
    try {
      setIsExportingPDF(true);
      const rows = await getExportSourceData();
      if (!rows.length) {
        return;
      }

      const doc = new jsPDF({ orientation: "landscape" });
      const headers = [
        "Order ID",
        "Name",
        "Total (GBP)",
        "Payments Completed",
        "Amount Paid (GBP)",
        "Amount Pending (GBP)",
        "Next Payment Date",
        "Status",
      ];

      const body = rows.map((row) => {
        const total = Math.max(0, Number(row?.installment_count) || 0);
        const paidRaw = Math.max(0, Number(row?.installment_paid) || 0);
        const paid = total > 0 ? Math.min(paidRaw, total) : paidRaw;
        return [
          row?.order_id ?? "",
          row?.customer_name ?? "",
          Number(row?.total_price || 0).toFixed(2),
          `${paid} of ${total}`,
          Number(row?.amount_paid || 0).toFixed(2),
          Number(row?.amount_pending || 0).toFixed(2),
          formatDate(row?.next_installment_date),
          deriveInstallmentStatus(row),
        ];
      });

      autoTable(doc, {
        head: [headers],
        body,
        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: "linebreak",
          cellWidth: "wrap",
        },
        headStyles: {
          fillColor: [0, 0, 0],
          textColor: 255,
          fontStyle: "bold",
        },
        bodyStyles: {
          valign: "top",
          textColor: [0, 0, 0],
        },
        margin: { top: 15 },
        didDrawPage: (data_) => {
          doc.setFontSize(12);
          doc.setTextColor(0, 0, 0);
          doc.text("Installment Orders", data_.settings.margin.left, 10);
          doc.setFontSize(8);
          const dateStr = new Date().toLocaleDateString("en-GB");
          doc.text(
            `Generated on: ${dateStr}`,
            data_.settings.margin.left,
            5
          );
        },
      });

      const timestamp = new Date().toISOString().slice(0, 10);
      doc.save(`installment-orders-${timestamp}.pdf`);
    } catch (error) {
      console.error("Error exporting installments to PDF:", error);
    } finally {
      setIsExportingPDF(false);
      setIsExportMenuOpen(false);
    }
  };

  const handleExportToExcel = async () => {
    try {
      setIsExportingExcel(true);
      const rows = await getExportSourceData();
      if (!rows.length) {
        return;
      }

      const sheetRows = rows.map((row) => {
        const total = Math.max(0, Number(row?.installment_count) || 0);
        const paidRaw = Math.max(0, Number(row?.installment_paid) || 0);
        const paid = total > 0 ? Math.min(paidRaw, total) : paidRaw;
        return {
          "Order ID": row?.order_id ?? "",
          Name: row?.customer_name ?? "",
          "Total (GBP)": Number(row?.total_price || 0).toFixed(2),
          "Payments Completed": `${paid} of ${total}`,
          "Amount Paid (GBP)": Number(row?.amount_paid || 0).toFixed(2),
          "Amount Pending (GBP)": Number(row?.amount_pending || 0).toFixed(2),
          "Next Payment Date": formatDate(row?.next_installment_date),
          Status: deriveInstallmentStatus(row),
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(sheetRows);
      worksheet["!cols"] = [
        { wch: 12 },
        { wch: 24 },
        { wch: 14 },
        { wch: 20 },
        { wch: 18 },
        { wch: 20 },
        { wch: 20 },
        { wch: 14 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Installments");

      const timestamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `installment-orders-${timestamp}.xlsx`);
    } catch (error) {
      console.error("Error exporting installments to Excel:", error);
    } finally {
      setIsExportingExcel(false);
      setIsExportMenuOpen(false);
    }
  };

  const fetchStatusOptions = async (search = "") => {
    if (!search || search.trim() === "") {
      return STATUS_OPTIONS;
    }
    const term = search.toLowerCase();
    return STATUS_OPTIONS.filter((o) => o.label.toLowerCase().includes(term));
  };

  const columns = useMemo(
    () => [
      {
        name: "Order ID",
        selector: (row) => row?.order_id ?? "—",
      },
      {
        name: "Name",
        selector: (row) => row?.customer_name || "—",
      },
      {
        name: "Total",
        cell: (row) => (
          <span>£ {Number(row?.total_price || 0).toFixed(2)}</span>
        ),
      },
      {
        name: "Payments Completed",
        cell: (row) => {
          const total = Math.max(0, Number(row?.installment_count) || 0);
          const paidRaw = Math.max(0, Number(row?.installment_paid) || 0);
          const paid = total > 0 ? Math.min(paidRaw, total) : paidRaw;
          return (
            <span className={styles.paymentsCell}>
              {paid} of {total}
            </span>
          );
        },
      },
      {
        name: "Amount Pending",
        cell: (row) => (
          <span>£ {Number(row?.amount_pending || 0).toFixed(2)}</span>
        ),
      },
      {
        name: "Next Payment Date",
        selector: (row) => formatDate(row?.next_installment_date),
      },
      {
        name: "Status",
        cell: (row) => {
          const status = deriveInstallmentStatus(row);
          return (
            <Badge label={status} badgeStylesOverride={STATUS_STYLES[status]} />
          );
        },
      },
      {
        name: "",
        cell: (row) =>
          row?.order_id ? (
            <FiEye
              size={18}
              style={{ cursor: "pointer" }}
              onClick={() => navigate(`/orders/details/${row.order_id}`)}
              title="View Details"
            />
          ) : null,
        width: "60px",
      },
    ],
    [navigate]
  );

  return (
    <>
      <div className={styles.headerRow}>
        <div ref={exportMenuRef} className={styles.exportWrapper}>
          <button
            type="button"
            className={styles.exportTrigger}
            onClick={() => setIsExportMenuOpen((prev) => !prev)}
            disabled={isExportingPDF || isExportingExcel}
            aria-haspopup="menu"
            aria-expanded={isExportMenuOpen}
            aria-label="Export options"
            title="Export"
          >
            <FiMoreVertical size={18} />
          </button>
          {isExportMenuOpen && (
            <div className={styles.exportMenu} role="menu">
              <button
                className={styles.exportMenuButton}
                onClick={handleExportToPDF}
                disabled={isExportingPDF || isExportingExcel}
              >
                {isExportingPDF ? (
                  <>
                    <span className={styles.loader} />
                    <span style={{ marginLeft: "8px" }}>Exporting PDF...</span>
                  </>
                ) : (
                  "Export to PDF"
                )}
              </button>
              <button
                className={styles.exportMenuButton}
                onClick={handleExportToExcel}
                disabled={isExportingPDF || isExportingExcel}
              >
                {isExportingExcel ? (
                  <>
                    <span className={styles.loader} />
                    <span style={{ marginLeft: "8px" }}>
                      Exporting Excel...
                    </span>
                  </>
                ) : (
                  "Export to Excel"
                )}
              </button>
            </div>
          )}
        </div>

        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Search by Order ID or Name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>

      <div className={styles.filtersRow}>
        <SelectSearch
          id="installment-event-filter"
          placeholder="Filter by Event"
          value={selectedEvent}
          onChange={(val) => setSelectedEvent(val)}
          loadOptions={fetchEventOptions}
          formGroupStyleOverride={{ width: "30rem", marginBottom: "0" }}
        />
        <SelectSearch
          id="installment-status-filter"
          placeholder="Filter by Status"
          value={selectedStatus}
          onChange={(val) => setSelectedStatus(val)}
          loadOptions={fetchStatusOptions}
          formGroupStyleOverride={{ width: "30rem", marginBottom: "0" }}
        />
      </div>

      <DataTable_
        handlefetchData={fetchInstallments}
        data={filteredData}
        totalCount={displayCount}
        columns={columns}
        ref={childRef}
        initialPage={initialFromUrl.page}
        initialPerPage={initialFromUrl.perPage}
        progressPending={isLoading}
        progressComponent={
          <div style={{ padding: "24px", color: "#666", fontSize: "1.4rem" }}>
            Loading installment orders...
          </div>
        }
      />
    </>
  );
};

export default InstallmentOrders;
